import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from '../../../components/widgets/Select';
import selectCss from '../../../components/widgets/ReactSelectOverrides.css';
import Button from '../../../components/atoms/Button';
import Toggle from '../../../components/atoms/Toggle';
import Condition from './Condition/Condition';
import WarningMessage from '../../../components/utilities/WarningMessage';
import { ACTION_DROPDOWN_OPTIONS, COLUMNS, NEW_RULE_ID, TRANSLATION_KEY } from '../constants';
import { findOption, isAdminSafeguarded } from '../utils';
import { ConditionOptionsShape, RuleShape } from '../propShapes';
import css from '../style.css';

const RuleEditor = ({
    draft,
    savedRule,
    conditionOptions,
    rejectSaveDisabled,
    readOnly,
    saving,
    translate,
    editRule,
    addCondition,
    editCondition,
    removeCondition,
    save,
    cancelEdit,
    deleteRule,
    duplicate,
}) => {
    const disabled = readOnly || !!draft.generated;
    const isNew = draft.id === NEW_RULE_ID;
    const adminSafeguard = isAdminSafeguarded({ disabled, rejectSaveDisabled, draft, savedRule });
    const hasCustomParam = (draft.conditions || []).some(cond => cond.dimension === 'custom_parameter');
    const disabledSubtitleKey = readOnly ? 'READ_ONLY_RULES' : 'GENERATED_RULES_READONLY';

    const actionOptions = ACTION_DROPDOWN_OPTIONS.map(option => ({
        value: option.value,
        label: translate(option.labelKey),
    }));

    const stop = useCallback(e => e.stopPropagation(), []);

    return (
        <>
            <tr className={css.editing} onClick={stop}>
                <td className={css.editorCell} colSpan={COLUMNS.length}>
                    {adminSafeguard && (
                        <div className={css.adminSafeguard}>{translate(`${TRANSLATION_KEY}.ADMIN_SAFEGUARD`)}</div>
                    )}

                    <div className={css.editorBody}>
                        <div className={css.conditionsColumn}>
                            <div className={css.sectionTitle}>
                                <b>{translate(`${TRANSLATION_KEY}.CONDITIONS_TITLE`)}</b>
                                {disabled && (
                                    <span className={css.subtitle}>
                                        {translate(`${TRANSLATION_KEY}.${disabledSubtitleKey}`)}
                                    </span>
                                )}
                            </div>

                            {(draft.conditions || []).map((cond, i) => (
                                <Condition
                                    key={cond.clientId}
                                    dimension={cond.dimension}
                                    operator={cond.operator}
                                    customFieldName={cond.customFieldName}
                                    values={cond.values}
                                    invalid={cond.invalid}
                                    first={i === 0}
                                    only={draft.conditions.length === 1}
                                    disabled={disabled}
                                    conditionOptions={conditionOptions}
                                    translate={translate}
                                    editCondition={(key, value) => editCondition(draft.id, i, key, value)}
                                    removeCondition={() => removeCondition(draft.id, i)}
                                />
                            ))}

                            {!disabled && (
                                <Button
                                    type="link"
                                    className={css.addCondition}
                                    Icon="plus"
                                    text={`${TRANSLATION_KEY}.ADD_CONDITION`}
                                    onClick={() => addCondition(draft.id)}
                                />
                            )}

                            {hasCustomParam && (
                                <div className={css.customFieldWarning}>
                                    <WarningMessage
                                        show
                                        type="warning"
                                        showIcon={false}
                                        message={translate(`${TRANSLATION_KEY}.CUSTOM_PARAM_WARNING`, null, {
                                            renderInnerHtml: true,
                                        })}
                                    />
                                </div>
                            )}
                        </div>

                        <div className={css.actionColumn}>
                            <div className={css.sectionTitle}>
                                <b>{translate(`${TRANSLATION_KEY}.ACTION_TITLE`)}</b>
                            </div>
                            <Select
                                className={classNames(selectCss.singularSelect, { [selectCss.error]: adminSafeguard })}
                                classNamePrefix="Select"
                                isDisabled={disabled}
                                isSearchable={false}
                                isClearable={false}
                                usePortal
                                value={findOption(actionOptions, draft.action)}
                                options={actionOptions}
                                onChange={option => editRule(draft.id, 'action', option.value)}
                            />
                        </div>

                        <div className={css.statusColumn}>
                            <div className={css.sectionTitle}>
                                <b>{translate(`${TRANSLATION_KEY}.RULE_STATUS_TITLE`)}</b>
                            </div>
                            <Toggle
                                checked={draft.active || false}
                                disabled={disabled}
                                onToggle={e => editRule(draft.id, 'active', e.target.checked)}
                                label={draft.active ? `${TRANSLATION_KEY}.ACTIVE` : `${TRANSLATION_KEY}.PAUSED`}
                            />
                        </div>
                    </div>
                </td>
            </tr>

            <tr className={css.editing} onClick={stop}>
                <td className={css.actionsCell} colSpan={COLUMNS.length}>
                    <div className={css.actionsBar}>
                        <Button
                            type="link"
                            disabled={draft.dirty || readOnly}
                            text={`${TRANSLATION_KEY}.DUPLICATE`}
                            onClick={() => duplicate(draft)}
                        />
                        {!isNew && (
                            <Button
                                type="link"
                                disabled={readOnly}
                                text={`${TRANSLATION_KEY}.DELETE`}
                                onClick={() => deleteRule(draft.id)}
                            />
                        )}
                        <div className={css.actionsBarRight}>
                            <Button type="link" text="STATIC.BUTTONS.CANCEL" onClick={() => cancelEdit(draft.id)} />
                            <Button
                                type="primary"
                                text="STATIC.BUTTONS.SAVE"
                                showSpinner={saving}
                                disabled={readOnly || !draft.dirty || adminSafeguard || saving}
                                onClick={() => save(draft.id)}
                            />
                        </div>
                    </div>
                </td>
            </tr>
        </>
    );
};

RuleEditor.propTypes = {
    draft: RuleShape.isRequired,
    savedRule: RuleShape,
    conditionOptions: ConditionOptionsShape.isRequired,
    rejectSaveDisabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    saving: PropTypes.bool,
    translate: PropTypes.func.isRequired,
    editRule: PropTypes.func.isRequired,
    addCondition: PropTypes.func.isRequired,
    editCondition: PropTypes.func.isRequired,
    removeCondition: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
    cancelEdit: PropTypes.func.isRequired,
    deleteRule: PropTypes.func.isRequired,
    duplicate: PropTypes.func.isRequired,
};

export default RuleEditor;
